import { FORM_ERROR } from 'final-form';
import { CREATED, OK } from 'http-status-codes';
import { put, select, takeLatest } from 'redux-saga/effects';

import { formatApiErrors, getObjectsDiff, safeResolveAsync } from '../../utils';

import * as api from './api';
import { actions, types } from './duck';
import { actions as couponImageActions } from '../couponImages/duck';

const stripReadOnlyAndBadKeys = obj => {
  const cleaned = { ...(obj || {}) };

  [
    'cities',
    'created_at',
    'is_affiliate_or_flyer',
    'business',
    'affiliate_or_flyer',
  ].forEach(k => {
    if (k in cleaned) delete cleaned[k];
  });

  if (Array.isArray(cleaned.subcategories)) {
    cleaned.subcategories = cleaned.subcategories
      .map(s => (s && typeof s === 'object' ? s.id : s))
      .filter(v => v !== null && v !== undefined && v !== '' && v !== 'None')
      .map(v => Number(v))
      .filter(n => Number.isFinite(n) && n > 0);
  }

  if (cleaned.category && typeof cleaned.category === 'object') {
    cleaned.category = cleaned.category.id;
  }

  return cleaned;
};

export function* createCoupon({ id, data, resolve }) {
  try {
    yield put(actions.createCouponRequest(id));

    const response = yield api.createCoupon(data);

    if (response.status === CREATED) {
      yield put(actions.createCouponSuccess(id, response.data));
      yield put(couponImageActions.clearCouponImagesState());
      yield safeResolveAsync(resolve, { success: true, data: response.data });
    } else {

      if (Array.isArray(response.data)) {
        yield put(actions.createCouponFailure(id, response.data[0]));
        yield safeResolveAsync(resolve, {
          success: false,
          error: response.data[0],
        });
      } else {
        yield put(actions.createCouponFailure(id, response.data));
        yield safeResolveAsync(resolve, {
          success: false,
          error: response.data.error,
        });
      }
      yield safeResolveAsync(resolve, formatApiErrors(response.data));
    }
  } catch (error) {
    yield put(actions.createCouponFailure(id, error.message));
    yield safeResolveAsync(resolve, { [FORM_ERROR]: error.message });
    yield safeResolveAsync(resolve, { success: false, error: error.message });
  }
}

export function* getCoupon({ id, resolve }) {
  try {
    yield put(actions.getCouponRequest(id));

    const response = yield api.getCoupon(id);

    if (response.status === OK) {
      yield put(actions.getCouponSuccess(id, response.data));
      yield safeResolveAsync(resolve, { success: true });
    } else {
      if (Array.isArray(response.data)) {
        yield put(actions.getCouponFailure(id, response.data[0]));
      } else {
        yield put(actions.getCouponFailure(id, response.data.detail));
      }
    }
  } catch (error) {
    yield put(actions.getCouponFailure(id, error.message));
  }
}

export function* editCoupon({ id, data, resolve }) {
  try {
    const originalData = yield select(state => state.couponBuilder[id]?.data);

    // sanitize both sides BEFORE diff
    const cleanOriginal = stripReadOnlyAndBadKeys(originalData);
    const cleanIncoming = stripReadOnlyAndBadKeys(data);

    const updatedData = getObjectsDiff(cleanOriginal, cleanIncoming);

    yield put(actions.editCouponRequest(id));

    const response = yield api.editCoupon(updatedData, cleanIncoming.id);

    if (response.status === OK) {
      yield put(actions.editCouponSuccess(id, response.data));
      yield put(couponImageActions.clearCouponImagesState());
      yield safeResolveAsync(resolve, { success: true });
    } else {
      if (Array.isArray(response.data)) {
        yield put(actions.editCouponFailure(id, response.data[0]));
      } else {
        yield put(actions.editCouponFailure(id, response.data.detail));
      }
      yield safeResolveAsync(resolve, formatApiErrors(response.data));
    }
  } catch (error) {
    yield put(actions.editCouponFailure(id, error.message));
    yield safeResolveAsync(resolve, { [FORM_ERROR]: error.message });
  }
}

export default function* saga() {
  yield takeLatest(types.CREATE_COUPON, createCoupon);
  yield takeLatest(types.GET_COUPON, getCoupon);
  yield takeLatest(types.EDIT_COUPON, editCoupon);
}
